import React, { useState } from 'react';
import { 
  Truck, Users, MapPin, Clock, DollarSign, Package, 
  CheckCircle2, AlertCircle, Navigation, TrendingUp,
  Calendar, Star, Phone
} from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';

// Mock driver data
const mockDriverStats = {
  totalDeliveries: 156,
  completedToday: 8,
  pendingDeliveries: 3,
  rating: 4.8,
  earnings: {
    today: 125.50,
    thisWeek: 876.25,
    thisMonth: 3250.00
  }
};

const mockPendingOrders = [
  {
    id: 'ORD-001',
    customer: 'John Smith',
    address: '123 Bank Street, Ottawa, ON K1P 5N2',
    items: 5,
    total: 45.99,
    time: '2:30 PM',
    distance: '3.2 km',
    status: 'ready'
  },
  {
    id: 'ORD-002',
    customer: 'Sarah Johnson',
    address: '456 Rideau Street, Ottawa, ON K1N 5Y3',
    items: 3,
    total: 28.50,
    time: '3:00 PM',
    distance: '5.1 km',
    status: 'preparing'
  },
  {
    id: 'ORD-003',
    customer: 'Mike Wilson',
    address: '789 Carling Avenue, Ottawa, ON K2A 1C4',
    items: 8,
    total: 89.99,
    time: '3:30 PM',
    distance: '7.8 km',
    status: 'ready'
  }
];

const DriversDashboard = ({ onNavigateToOnboarding, onNavigateToNavigation }) => {
  const [activeTab, setActiveTab] = useState('overview');

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-3">
            <Truck className="w-8 h-8 text-blue-500" />
            Driver Dashboard
          </h1>
          <p className="text-gray-500 mt-1">Manage deliveries and track your performance</p>
        </div>
        <div className="flex gap-3">
          <Button 
            onClick={onNavigateToOnboarding}
            variant="outline"
            className="flex items-center gap-2"
          >
            <Users className="w-4 h-4" />
            Onboarding
          </Button>
          <Button 
            onClick={onNavigateToNavigation}
            className="bg-green-500 hover:bg-green-600 text-white flex items-center gap-2"
          >
            <Navigation className="w-4 h-4" />
            Start Navigation
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Today&apos;s Deliveries</p>
              <p className="text-3xl font-bold text-gray-800">{mockDriverStats.completedToday}</p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
              <CheckCircle2 className="w-6 h-6 text-green-600" />
            </div>
          </div>
          <p className="text-sm text-green-600 mt-2">+2 from yesterday</p>
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Pending Orders</p>
              <p className="text-3xl font-bold text-gray-800">{mockDriverStats.pendingDeliveries}</p>
            </div>
            <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
              <Package className="w-6 h-6 text-orange-600" />
            </div>
          </div>
          <p className="text-sm text-orange-600 mt-2">3 ready for pickup</p>
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Today&apos;s Earnings</p>
              <p className="text-3xl font-bold text-gray-800">${mockDriverStats.earnings.today.toFixed(2)}</p>
            </div>
            <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
              <DollarSign className="w-6 h-6 text-blue-600" />
            </div>
          </div>
          <p className="text-sm text-blue-600 mt-2">Week: ${mockDriverStats.earnings.thisWeek.toFixed(2)}</p>
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Driver Rating</p>
              <p className="text-3xl font-bold text-gray-800">{mockDriverStats.rating}</p>
            </div>
            <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center">
              <Star className="w-6 h-6 text-yellow-600 fill-yellow-600" />
            </div>
          </div>
          <p className="text-sm text-yellow-600 mt-2">Based on 156 deliveries</p>
        </Card>
      </div>

      {/* Pending Deliveries */}
      <Card className="p-6 mb-8">
        <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
          <Package className="w-5 h-5 text-blue-500" />
          Pending Deliveries
        </h2>
        <div className="space-y-4">
          {mockPendingOrders.map((order) => (
            <div 
              key={order.id}
              className="flex items-center justify-between p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors"
            >
              <div className="flex items-center gap-4">
                <div className={`w-3 h-3 rounded-full ${
                  order.status === 'ready' ? 'bg-green-500' : 'bg-orange-500'
                }`} />
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-gray-800">{order.id}</span>
                    <Badge className={
                      order.status === 'ready' 
                        ? 'bg-green-100 text-green-700' 
                        : 'bg-orange-100 text-orange-700'
                    }>
                      {order.status === 'ready' ? 'Ready for Pickup' : 'Preparing'}
                    </Badge>
                  </div>
                  <p className="text-sm text-gray-600">{order.customer}</p>
                  <p className="text-xs text-gray-500 flex items-center gap-1 mt-1">
                    <MapPin className="w-3 h-3" />
                    {order.address}
                  </p>
                </div>
              </div>
              <div className="text-right">
                <p className="font-semibold text-gray-800">${order.total.toFixed(2)}</p>
                <p className="text-sm text-gray-500">{order.items} items</p>
                <div className="flex items-center gap-2 mt-1 text-xs text-gray-500">
                  <Clock className="w-3 h-3" />
                  {order.time}
                  <span>•</span>
                  <MapPin className="w-3 h-3" />
                  {order.distance}
                </div>
              </div>
              <Button 
                onClick={onNavigateToNavigation}
                className="ml-4 bg-blue-500 hover:bg-blue-600 text-white"
                disabled={order.status !== 'ready'}
              >
                <Navigation className="w-4 h-4 mr-2" />
                Navigate
              </Button>
            </div>
          ))}
        </div>
      </Card>

      {/* Weekly Performance */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="p-6">
          <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-green-500" />
            Weekly Performance
          </h2>
          <div className="space-y-4">
            {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day, index) => {
              const deliveries = [12, 15, 10, 18, 14, 22, 8][index];
              const maxDeliveries = 25;
              return (
                <div key={day} className="flex items-center gap-4">
                  <span className="w-10 text-sm text-gray-500">{day}</span>
                  <div className="flex-1 h-4 bg-gray-100 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-blue-500 to-green-500 rounded-full"
                      style={{ width: `${(deliveries / maxDeliveries) * 100}%` }}
                    />
                  </div>
                  <span className="w-8 text-sm font-medium text-gray-700">{deliveries}</span>
                </div>
              );
            })}
          </div>
        </Card>

        <Card className="p-6">
          <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
            <Calendar className="w-5 h-5 text-purple-500" />
            Quick Actions
          </h2>
          <div className="grid grid-cols-2 gap-4">
            <button className="p-4 bg-blue-50 rounded-xl hover:bg-blue-100 transition-colors text-left">
              <Package className="w-8 h-8 text-blue-500 mb-2" />
              <p className="font-semibold text-gray-800">View All Orders</p>
              <p className="text-sm text-gray-500">See delivery history</p>
            </button>
            <button className="p-4 bg-green-50 rounded-xl hover:bg-green-100 transition-colors text-left">
              <DollarSign className="w-8 h-8 text-green-500 mb-2" />
              <p className="font-semibold text-gray-800">Earnings Report</p>
              <p className="text-sm text-gray-500">View detailed earnings</p>
            </button>
            <button className="p-4 bg-purple-50 rounded-xl hover:bg-purple-100 transition-colors text-left">
              <Star className="w-8 h-8 text-purple-500 mb-2" />
              <p className="font-semibold text-gray-800">Customer Feedback</p>
              <p className="text-sm text-gray-500">View ratings & reviews</p>
            </button>
            <button 
              onClick={onNavigateToOnboarding}
              className="p-4 bg-orange-50 rounded-xl hover:bg-orange-100 transition-colors text-left"
            >
              <Users className="w-8 h-8 text-orange-500 mb-2" />
              <p className="font-semibold text-gray-800">Update Profile</p>
              <p className="text-sm text-gray-500">Edit driver details</p>
            </button>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default DriversDashboard;
